import { ArrowUpDown, ChevronDown, ChevronRight, Edit2, Plus, Settings, Trash2 } from 'lucide-react'
import * as React from 'react'

import useMediaQuery from '@/hooks/use-media-query'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

import { useAuth } from '@/contexts/auth-context'
import { useForumUnread } from '@/contexts/forum-unread-context'
import { Role } from '@/enums/role.enum'

import ChannelItem from './channel-item'

import { Channel, GroupChannel } from '@/types/group-channel.type'

interface GroupItemProps {
  group: GroupChannel & { isExpanded: boolean }
  activeChannelId: string | null
  onToggleGroup: (groupId: string) => void
  onSelectChannel: (channel: Channel) => void
  onAddChannel: (group: GroupChannel) => void
  onEditGroup: (group: GroupChannel) => void
  onDeleteGroup: (group: GroupChannel) => void
  onEditChannel: (channel: Channel) => void
  onDeleteChannel: (channel: Channel) => void
  onSelectGroup: (group: GroupChannel) => void
  onReorderChannels: (group: GroupChannel) => void
}

export default function GroupItem({
  group,
  activeChannelId,
  onToggleGroup,
  onSelectChannel,
  onAddChannel,
  onEditGroup,
  onDeleteGroup,
  onEditChannel,
  onDeleteChannel,
  onSelectGroup,
  onReorderChannels,
}: GroupItemProps) {
  const { user } = useAuth()
  const isAdmin = user?.roles.includes(Role.ADMIN)
  const { getUnread, resetChannel } = useForumUnread()

  return (
    <div className="mb-3 sm:mb-4 relative">
      <div className="flex items-center w-full px-3 sm:px-4 py-1 sm:py-1.5 text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent duration-200 rounded-md group transition-all relative">
        <div className="flex flex-1 items-center cursor-pointer min-w-0" onClick={() => onToggleGroup(group.id)}>
          {group.isExpanded ? (
            <ChevronDown className="w-3 h-3 mr-1 sm:mr-2 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-3 h-3 mr-1 sm:mr-2 flex-shrink-0" />
          )}
          <span className="text-muted-foreground uppercase tracking-wide text-xs truncate">{group.name}</span>
        </div>
        {isAdmin && (
          <GroupActions
            group={group}
            onAddChannel={onAddChannel}
            onEditGroup={onEditGroup}
            onDeleteGroup={onDeleteGroup}
            onReorderChannels={onReorderChannels}
          />
        )}
      </div>

      {group.isExpanded && (
        <ul className="mt-1">
          {group.channels.map(channel => (
            <ChannelItem
              key={channel.id}
              channel={channel}
              isActive={channel.id === activeChannelId}
              onSelect={() => {
                onSelectChannel(channel)
                onSelectGroup(group)
                resetChannel(channel.id)
              }}
              onEdit={() => {
                onEditChannel(channel)
                onSelectGroup(group)
              }}
              onDelete={() => {
                onDeleteChannel(channel)
                onSelectGroup(group)
              }}
              unreadCount={getUnread(channel.id)}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

interface GroupActionsProps {
  group: GroupChannel
  onAddChannel: (group: GroupChannel) => void
  onEditGroup: (group: GroupChannel) => void
  onDeleteGroup: (group: GroupChannel) => void
  onReorderChannels: (group: GroupChannel) => void
}

function GroupActions({ group, onAddChannel, onEditGroup, onDeleteGroup, onReorderChannels }: GroupActionsProps) {
  const isMobile = useMediaQuery('(max-width: 640px)')
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false)
  return (
    <Popover modal={isMobile} open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 hover:bg-blue-100 hover:text-blue-800"
        >
          <Settings className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-2 z-[1000000]">
        <button
          onClick={() => {
            onAddChannel(group)
            isMobile && setIsMobileSidebarOpen(false)
          }}
          className="flex items-center w-full gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Thêm kênh mới</span>
        </button>
        <button
          onClick={() => {
            onReorderChannels(group)
            isMobile && setIsMobileSidebarOpen(false)
          }}
          className="flex items-center w-full gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
        >
          <ArrowUpDown className="h-4 w-4" />
          <span>Sắp xếp kênh</span>
        </button>
        <button
          onClick={() => {
            onEditGroup(group)
            isMobile && setIsMobileSidebarOpen(false)
          }}
          className="flex items-center w-full gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
        >
          <Edit2 className="h-4 w-4" />
          <span>Chỉnh sửa</span>
        </button>
        <button
          onClick={() => {
            onDeleteGroup(group)
            isMobile && setIsMobileSidebarOpen(false)
          }}
          className="flex items-center w-full gap-2 px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
        >
          <Trash2 className="h-4 w-4" />
          <span>Xóa</span>
        </button>
      </PopoverContent>
    </Popover>
  )
}
